import { apiSlice } from './apiSlice';

const EOM_URL = '/api/eom';

export const eomApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({

    // ── Admin ──────────────────────────────────────────────────────────────

    getEomSummary: builder.query({
      query: () => ({ url: `${EOM_URL}/admin/summary` }),
      providesTags: ['MonthlyStatement'],
      keepUnusedDataFor: 30,
    }),

    getUserStatements: builder.query({
      query: (userId) => ({ url: `${EOM_URL}/admin/${userId}/statements` }),
      providesTags: (_res, _err, userId) => [
        'MonthlyStatement',
        { type: 'MonthlyStatement', id: userId },
      ],
      keepUnusedDataFor: 10,
    }),

    generateStatement: builder.mutation({
      query: ({ userId, month, force = false }) => ({
        url: `${EOM_URL}/admin/${userId}/statements`,
        method: 'POST',
        body: { month, force },
      }),
      invalidatesTags: (_res, _err, { userId }) => [
        'MonthlyStatement',
        { type: 'MonthlyStatement', id: userId },
      ],
    }),

    sendStatement: builder.mutation({
      query: (statementId) => ({
        url: `${EOM_URL}/admin/statements/${statementId}/send`,
        method: 'POST',
      }),
      invalidatesTags: (_res, _err, statementId) => [
        { type: 'MonthlyStatement', id: statementId },
        'MonthlyStatement',
      ],
    }),

    updateStatement: builder.mutation({
      query: ({ statementId, ...fields }) => ({
        url: `${EOM_URL}/admin/statements/${statementId}`,
        method: 'PUT',
        body: fields,
      }),
      invalidatesTags: (_res, _err, { statementId }) => [
        { type: 'MonthlyStatement', id: statementId },
        'MonthlyStatement',
      ],
    }),

    recordPayment: builder.mutation({
      query: ({ statementId, ...fields }) => ({
        url: `${EOM_URL}/admin/statements/${statementId}/pay`,
        method: 'PUT',
        body: fields,
      }),
      invalidatesTags: ['MonthlyStatement'],
    }),

    unrecordPayment: builder.mutation({
      query: (statementId) => ({
        url: `${EOM_URL}/admin/statements/${statementId}/unpay`,
        method: 'PUT',
      }),
      invalidatesTags: ['MonthlyStatement'],
    }),

    deleteStatement: builder.mutation({
      query: (statementId) => ({
        url: `${EOM_URL}/admin/statements/${statementId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MonthlyStatement'],
    }),

    // ── Admin — order management ───────────────────────────────────────────

    searchUserOrders: builder.query({
      query: ({ userId, q }) => ({
        url: `${EOM_URL}/admin/${userId}/orders/search`,
        params: { q },
      }),
      keepUnusedDataFor: 0,
    }),

    flagOrderAsEom: builder.mutation({
      query: (orderId) => ({
        url: `${EOM_URL}/admin/orders/${orderId}/flag-eom`,
        method: 'PATCH',
      }),
      invalidatesTags: ['MonthlyStatement'],
    }),

    resendEomInvoice: builder.mutation({
      query: (orderId) => ({
        url: `${EOM_URL}/admin/orders/${orderId}/invoice/resend`,
        method: 'POST',
      }),
    }),

    addOrderToStatement: builder.mutation({
      query: ({ statementId, orderId }) => ({
        url: `${EOM_URL}/admin/statements/${statementId}/orders`,
        method: 'POST',
        body: { orderId },
      }),
      invalidatesTags: ['MonthlyStatement'],
    }),

    removeOrderFromStatement: builder.mutation({
      query: ({ statementId, orderId }) => ({
        url: `${EOM_URL}/admin/statements/${statementId}/orders/${orderId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MonthlyStatement'],
    }),

    // ── Customer ───────────────────────────────────────────────────────────

    getMyStatements: builder.query({
      query: () => ({ url: `${EOM_URL}/mine` }),
      providesTags: ['MonthlyStatement'],
      keepUnusedDataFor: 10,
    }),

    payStatementOnline: builder.mutation({
      query: ({ statementId, details }) => ({
        url: `${EOM_URL}/statements/${statementId}/pay-online`,
        method: 'POST',
        body: { details },
      }),
      invalidatesTags: ['MonthlyStatement'],
    }),

  }),
});

export const {
  useGetEomSummaryQuery,
  useGetUserStatementsQuery,
  useGenerateStatementMutation,
  useSendStatementMutation,
  useUpdateStatementMutation,
  useRecordPaymentMutation,
  useUnrecordPaymentMutation,
  useDeleteStatementMutation,
  useGetMyStatementsQuery,
  usePayStatementOnlineMutation,
  useSearchUserOrdersQuery,
  useFlagOrderAsEomMutation,
  useResendEomInvoiceMutation,
  useAddOrderToStatementMutation,
  useRemoveOrderFromStatementMutation,
} = eomApiSlice;
